import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ArchiveFolderCreatePayload,
  ArchiveFolderIncluded,
  ArchiveFolderResource,
  ArchiveFoldersResponse
} from '@type/archive.types'
import type { GeneralResMock } from '@type/general.type'
import { AXIOS_INSTANCE } from '../api'

type Props = {
  page?: number
  limit?: number
  query?: string
  sortDir?: 'asc' | 'desc'
  sortBy?: string
  id?: string
  digitization_status?: string
  facility_id?: string
  patient_id?: string
  folder_number?: string
  enabled?: boolean
  stat?: boolean
}

export const useArchives = (arg: Partial<Props> = {}) => {
  const {
    page = 1,
    limit = 10,
    query,
    sortDir,
    sortBy,
    id,
    digitization_status,
    facility_id,
    patient_id,
    folder_number,
    enabled = true,
    stat = true
  } = arg

  const { data, isLoading, error } = useQuery({
    enabled: enabled && !id,
    queryKey: [
      'archives',
      page,
      limit,
      query,
      sortDir,
      sortBy,
      digitization_status,
      facility_id,
      patient_id,
      folder_number,
      stat
    ],
    queryFn: async () => {
      const { data: response } = await AXIOS_INSTANCE.get<ArchiveFoldersResponse>('/archive_folders', {
        params: {
          'page[number]': page,
          'page[size]': limit,
          ...(query && { query }),
          ...(sortDir && { sort_dir: sortDir }),
          ...(sortBy && { sort_by: sortBy }),
          ...(digitization_status && { digitization_status }),
          ...(facility_id && { facility_id }),
          ...(patient_id && { patient_id }),
          ...(folder_number && { folder_number }),
          ...(stat !== undefined && { stat })
        }
      })
      return response
    }
  })

  const queryClient = useQueryClient()

  const { mutateAsync: createArchiveFolder, isPending: isCreating } = useMutation({
    mutationFn: async (payload: ArchiveFolderCreatePayload) => {
      const { data: response } = await AXIOS_INSTANCE.post('/archive_folders', payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archives'] })
    }
  })

  const { data: singleData, isLoading: singleIsLoading } = useQuery({
    enabled: enabled && !!id,
    queryKey: ['archives', id],
    queryFn: async () => {
      const { data: response } = await AXIOS_INSTANCE.get<
        GeneralResMock<ArchiveFolderResource, ArchiveFolderIncluded[]>
      >(`/archive_folders/${id}`)
      return response
    }
  })

  return {
    archives: data?.data,
    archive: singleData?.data,
    included: data?.included,
    singleIncluded: singleData?.included,
    pagination: data?.meta?.pagination,
    stats: data?.meta?.stats,
    isLoading: isLoading || singleIsLoading,
    isCreating,
    createArchiveFolder,
    error
  }
}
